import { useCallback } from "react";

import { useApolloClient } from "@apollo/client";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { CofConfigId } from "../../gql-generated/types";
import { getCreatorVerificationStatus } from "../api/queries";
import {
    type CreatorVerificationStatusQuery,
    type CreatorVerificationStatusQueryVariables,
} from "../api/queries/getCreatorVerificationStatus.gql-generated";

// Fails open: only an explicit `false` is unverified, so a null value (Atlas down) or an
// absent field never blocks enrollment.
export const getHasVerifiedPhone = (data: CreatorVerificationStatusQuery | undefined): boolean =>
    data?.user.creatorVerificationStatus?.hasVerifiedPhone !== false;

export interface CreatorVerification {
    required: boolean;
    checkHasVerifiedPhone: () => Promise<boolean>;
}

/**
 * Phone-verification status for the Lens+/exclusive enrollment gate. `checkHasVerifiedPhone`
 * is `network-only` and called per click, so a creator who just verified in the Snapchat app
 * is never blocked by a cached `false`.
 */
export const useCreatorVerification = (): CreatorVerification => {
    const client = useApolloClient();
    const { configs } = useFeatureGates();
    const required = configs[CofConfigId.MyLensesLensPlusPhoneVerification].enabled;

    const checkHasVerifiedPhone = useCallback(async (): Promise<boolean> => {
        try {
            const { data } = await client.query<
                CreatorVerificationStatusQuery,
                CreatorVerificationStatusQueryVariables
            >({ query: getCreatorVerificationStatus, fetchPolicy: "network-only" });
            return getHasVerifiedPhone(data);
        } catch {
            // Fail open — includes the field being absent until LOOK-171630 deploys.
            return true;
        }
    }, [client]);

    return { required, checkHasVerifiedPhone };
};
